import { useState } from "react";
import { Send, CheckCircle2, AlertCircle, Loader2 } from "lucide-react";

export default function ContactForm() {
  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    email: "",
    description: "",
    website_url: "", // Honeypot field
  });

  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState("idle"); // idle, loading, success, error
  const [errorMessage, setErrorMessage] = useState("");

  const validate = () => {
    const newErrors = {};
    if (!formData.name.trim()) {
      newErrors.name = "Navn er påkrævet";
    }
    if (!formData.phone.trim()) {
      newErrors.phone = "Telefonnummer er påkrævet";
    } else if (!/^[0-9+\s]{8,15}$/.test(formData.phone.trim())) {
      newErrors.phone = "Indtast venligst et gyldigt telefonnummer";
    }
    if (!formData.email.trim()) {
      newErrors.email = "E-mail er påkrævet";
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = "Indtast venligst en gyldig e-mailadresse";
    }
    if (!formData.description.trim()) {
      newErrors.description = "Beskrivelse af opgaven er påkrævet";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    setStatus("loading");
    setErrorMessage("");

    try {
      const response = await fetch("https://api.dev.auxo.dk/webform/submit", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(formData),
      });

      const result = await response.json();

      if (response.ok && result.status === "ok") {
        setStatus("success");
        setFormData({
          name: "",
          phone: "",
          email: "",
          description: "",
          website_url: "",
        });
      } else {
        setStatus("error");
        setErrorMessage(result.message || "Der skete en fejl under afsendelsen. Prøv venligst igen.");
      }
    } catch (err) {
      setStatus("error");
      setErrorMessage("Netværksfejl. Kontroller din internetforbindelse og prøv igen.");
    }
  };

  if (status === "success") {
    return (
      <div className="rounded-lg border border-emerald-500/30 bg-emerald-950/20 p-8 text-center border-glow">
        <CheckCircle2 className="mx-auto h-12 w-12 text-emerald-500 mb-4" />
        <h3 className="text-xl font-bold text-white mb-2">Tak for din henvendelse!</h3>
        <p className="text-gray-400 text-sm leading-relaxed max-w-md mx-auto">
          Vi har modtaget dine oplysninger og vil gennemgå din opgave hurtigst muligt. Vi kontakter dig typisk inden for 24 timer på hverdage.
        </p>
        <button
          onClick={() => setStatus("idle")}
          className="mt-6 inline-flex items-center justify-center rounded bg-brand-accent px-6 py-2.5 text-xs font-bold uppercase tracking-wider text-white transition-colors hover:bg-brand-accent-hover"
        >
          Send en ny besked
        </button>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-5 rounded-none border border-gray-200 bg-gray-50 p-6 sm:p-8 shadow-sm">
      {status === "error" && (
        <div className="flex items-start gap-3 rounded border border-red-500/30 bg-red-50 p-4 text-sm text-red-600">
          <AlertCircle className="h-5 w-5 shrink-0" />
          <div>
            <h4 className="font-semibold">Fejl ved afsendelse</h4>
            <p className="mt-0.5 text-xs text-red-600/90">{errorMessage}</p>
          </div>
        </div>
      )}

      {/* Honeypot field - ABSOLUTELY HIDDEN from humans */}
      <div className="sr-only absolute -top-96 -left-96" aria-hidden="true">
        <label htmlFor="website_url">Website URL</label>
        <input
          id="website_url"
          type="text"
          name="website_url"
          value={formData.website_url}
          onChange={handleChange}
          tabIndex="-1"
          autoComplete="off"
        />
      </div>

      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
        {/* Name */}
        <div className="space-y-1.5">
          <label htmlFor="name" className="block text-xs font-bold uppercase tracking-wider text-brand-charcoal">
            Navn <span className="text-brand-accent">*</span>
          </label>
          <input
            id="name"
            type="text"
            name="name"
            value={formData.name}
            onChange={handleChange}
            placeholder="Dit fulde navn"
            className={`w-full rounded-none border-b border-t-0 border-l-0 border-r-0 bg-transparent px-0 py-3 text-sm text-brand-charcoal placeholder-gray-400 outline-none transition-colors focus:border-brand-accent focus:ring-0 ${
              errors.name ? "border-red-500 focus:border-red-500" : "border-gray-300"
            }`}
          />
          {errors.name && <p className="text-xs text-red-500">{errors.name}</p>}
        </div>

        {/* Phone */}
        <div className="space-y-1.5">
          <label htmlFor="phone" className="block text-xs font-bold uppercase tracking-wider text-brand-charcoal">
            Telefonnummer <span className="text-brand-accent">*</span>
          </label>
          <input
            id="phone"
            type="tel"
            name="phone"
            value={formData.phone}
            onChange={handleChange}
            placeholder="F.eks. 29248263"
            className={`w-full rounded-none border-b border-t-0 border-l-0 border-r-0 bg-transparent px-0 py-3 text-sm text-brand-charcoal placeholder-gray-400 outline-none transition-colors focus:border-brand-accent focus:ring-0 ${
              errors.phone ? "border-red-500 focus:border-red-500" : "border-gray-300"
            }`}
          />
          {errors.phone && <p className="text-xs text-red-500">{errors.phone}</p>}
        </div>
      </div>

      {/* Email */}
      <div className="space-y-1.5">
        <label htmlFor="email" className="block text-xs font-bold uppercase tracking-wider text-brand-charcoal">
          E-mailadresse <span className="text-brand-accent">*</span>
        </label>
        <input
          id="email"
          type="email"
          name="email"
          value={formData.email}
          onChange={handleChange}
          placeholder="din@email.dk"
          className={`w-full rounded-none border-b border-t-0 border-l-0 border-r-0 bg-transparent px-0 py-3 text-sm text-brand-charcoal placeholder-gray-400 outline-none transition-colors focus:border-brand-accent focus:ring-0 ${
            errors.email ? "border-red-500 focus:border-red-500" : "border-gray-300"
          }`}
        />
        {errors.email && <p className="text-xs text-red-500">{errors.email}</p>}
      </div>

      {/* Description */}
      <div className="space-y-1.5">
        <label htmlFor="description" className="block text-xs font-bold uppercase tracking-wider text-brand-charcoal">
          Beskrivelse af opgaven <span className="text-brand-accent">*</span>
        </label>
        <textarea
          id="description"
          name="description"
          value={formData.description}
          onChange={handleChange}
          rows="4"
          placeholder="Beskriv dit projekt... F.eks. udgravning til nybyggeri, etablering af sandpude eller terrænregulering."
          className={`w-full rounded-none border-b border-t-0 border-l-0 border-r-0 bg-transparent px-0 py-3 text-sm text-brand-charcoal placeholder-gray-400 outline-none transition-colors focus:border-brand-accent focus:ring-0 resize-none ${
            errors.description ? "border-red-500 focus:border-red-500" : "border-gray-300"
          }`}
        />
        {errors.description && <p className="text-xs text-red-500">{errors.description}</p>}
      </div>

      {/* Submit Button */}
      <button
        type="submit"
        disabled={status === "loading"}
        className="flex w-full items-center justify-center gap-2 rounded-none bg-brand-accent py-3.5 text-xs font-bold uppercase tracking-wider text-white transition-all hover:bg-brand-accent-hover disabled:opacity-50 disabled:cursor-not-allowed hover:scale-[1.01] active:scale-[0.99]"
      >
        {status === "loading" ? (
          <>
            <Loader2 className="h-4 w-4 animate-spin" />
            Sender oplysninger...
          </>
        ) : (
          <>
            <Send className="h-4 w-4" />
            Indsend forespørgsel
          </>
        )}
      </button>
    </form>
  );
}
